import type { ReactNode } from "react";
import { DownOutlined, MoreOutlined } from "@ant-design/icons";
import { Dropdown, type MenuProps } from "./Overlay";
import { Button } from "./Button";
import { useUiText } from "./uiText";

/** Menüeintrag wie bei Antd (`key`, `label`, `icon`, `danger`, `disabled`, Trenner, eigenes `onClick`). */
export type ActionMenuItem = NonNullable<MenuProps["items"]>[number];

export type ActionMenuProps = {
  /** Beschriftung, z. B. „Aktionen“. Ohne Beschriftung erscheint nur `⋯` (enge Zeilen) mit `aria-label`. */
  label?: ReactNode;
  items: ActionMenuItem[];
  /** Symbol vor der Beschriftung (z. B. Herunterladen, Hinzufügen); ohne Beschriftung ersetzt es `⋯`. */
  icon?: ReactNode;
  /** Knopfart wie beim Kit-`Button`; Hauptaktion eines Bereichs `primary`. */
  type?: "primary" | "default" | "text";
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
  /** Zugänglicher Name bei reinem Symbol; Default „Aktionen“. */
  "aria-label"?: string;
  /** Gemeinsamer Klick-Handler; alternativ `onClick` je Eintrag. */
  onAction?: (key: string) => void;
};

/**
 * Einziger Auslöser für ein Menü aus Aktionen (14.10.2026): mit Beschriftung „[Symbol] Text ▾“ wie
 * Ant Design, ohne Beschriftung nur das Symbol (Standard `⋯`). Öffnet immer per Klick. Produkte bauen
 * keine eigenen `Dropdown`-Auslöser (`check-design-rules.mjs`, Regel `action-menu-trigger`).
 */
export function ActionMenu({ label, items, icon, type, block, loading, disabled, size, onAction, "aria-label": ariaLabel }: ActionMenuProps) {
  const { text } = useUiText();
  const iconOnly = label == null || label === false || label === "";
  return (
    <Dropdown trigger={["click"]} disabled={disabled} menu={{ items, onClick: onAction ? ({ key }) => onAction(String(key)) : undefined }}>
      {iconOnly ? (
        <Button type={type} icon={icon ?? <MoreOutlined />} loading={loading} size={size} disabled={disabled} aria-label={ariaLabel ?? text("Actions")} title={ariaLabel} />
      ) : (
        <Button type={type} block={block} icon={icon} loading={loading} size={size} disabled={disabled}>
          {label}
          <DownOutlined className="ck-action-menu-caret" aria-hidden />
        </Button>
      )}
    </Dropdown>
  );
}
